import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext/useEffectiveExperience';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import type { Destination } from '../types';
import type {
  SubscribeExperienceToCalendarMutation,
  SubscribeExperienceToCalendarMutationVariables,
} from './useSubscribeExperienceToCalendar.generated';

const query = gql`
  mutation SubscribeExperienceToCalendar(
    $input: SubscribeExperienceToCalendarInput!
  ) {
    subscribeExperienceToCalendar(input: $input) {
      id
      experienceId
      platform
      integrationId
      calendarId
      createdAt
    }
  }
`;

// Subscribes the viewer's calendar to the whole experience: the server exports
// every current and future event and keeps pushing new ones. The subscribed
// state rides Experience.viewerCalendarSubscriptions, so refetch the
// experience by both of its cache identities (id in edit mode, slug on the
// published view).
export const useSubscribeExperienceToCalendar = () => {
  const queryClient = useQueryClient();
  const experience = useEffectiveExperience();

  const { mutate, ...rest } = useMutation<
    SubscribeExperienceToCalendarMutation,
    QueryError,
    SubscribeExperienceToCalendarMutationVariables
  >({
    mutationFn: (variables) => gqlClient.request(query, variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createExperienceQueryKey(experience.id),
        exact: true,
      });
      if (experience.publishedSlug) {
        queryClient.invalidateQueries({
          queryKey: createExperienceQueryKey(experience.publishedSlug),
          exact: true,
        });
      }
    },
  });

  const subscribeToCalendar = (
    destination: Destination,
    options?: MutateOptions<
      SubscribeExperienceToCalendarMutation,
      QueryError,
      SubscribeExperienceToCalendarMutationVariables
    >
  ) =>
    mutate(
      {
        input: {
          experienceId: experience.id,
          integrationId: destination.integration.id,
          calendarId: destination.calendar.id,
        },
      },
      options
    );

  return { subscribeToCalendar, ...rest };
};
